import { useState, FormEvent, useEffect } from 'react';
// MUI Core Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';

// MUI Icons
import AutorenewIcon from '@mui/icons-material/Autorenew';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TimerIcon from '@mui/icons-material/Timer';

// Third-party libraries
import { motion } from 'framer-motion';

// Local imports
import { analyzeWebsite, getAnalysis, getGoodUrls, getAnalysisStatus } from '../services/api';
import AnalysisResults from '../components/AnalysisResults';
import { AnalysisResult } from '../types/analysis';
import JsonViewer from '../components/JsonViewer';

// Format milliseconds to readable time
const formatTime = (ms: number): string => {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  
  if (minutes === 0) {
    return `${seconds}s`;
  } else {
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }
};

export default function Home() {
  const [url, setUrl] = useState('');
  const [numPages, setNumPages] = useState('2');
  const [includeHtml, setIncludeHtml] = useState(false);
  const [includeFullText, setIncludeFullText] = useState(false);
  const [customRequestId, setCustomRequestId] = useState('');
  const [forceRefresh, setForceRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<{
    statusCode?: number;
    requestPayload?: string;
    responseData?: any;
  } | null>(null);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [analysisProgress, setAnalysisProgress] = useState<number>(0);
  const [progressStage, setProgressStage] = useState<string>('Initializing analysis...');
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState<number>(3);
  const [analysisCompleted, setAnalysisCompleted] = useState<boolean>(false);
  const [goodUrlsFound, setGoodUrlsFound] = useState<boolean>(false);
  const theme = useTheme();

  // Poll for updates when there's an active requestId
  useEffect(() => {
    if (!requestId || !loading || analysisCompleted) return;
    
    // Track initial polling time to implement faster polling at first
    const pollingStartTime = Date.now();
    let pollCount = 0;
    
    const fetchStatus = async () => {
      try {
        const analysisResult = await getAnalysis(requestId);
        setLastUpdateTime(new Date());
        
        if (analysisResult) {
          // Updating progress information
          if (analysisResult.processLogs && analysisResult.processLogs.length > 0) {
            const totalSteps = 5; // Approximate number of main steps
            const completedSteps = Math.min(analysisResult.processLogs.length, totalSteps);
            setAnalysisProgress((completedSteps / totalSteps) * 100);
            
            // Get the latest log message as the current stage
            const latestLog = analysisResult.processLogs[analysisResult.processLogs.length - 1];
            if (latestLog) {
              setProgressStage(latestLog.message);
            }
          }
          
          // Update the result state with what we have so far
          setResult(prevResult => {
            if (!prevResult) return analysisResult;
            // Keep goodUrls if we already have them and the new result doesn't
            const updatedGoodUrls = (analysisResult.goodUrls && analysisResult.goodUrls.length > 0) 
              ? analysisResult.goodUrls 
              : (prevResult.goodUrls || []);
            
            return { 
              ...prevResult, 
              ...analysisResult,
              goodUrls: updatedGoodUrls
            };
          });
          
          // Check if result has goodUrls
          if (analysisResult.goodUrls && analysisResult.goodUrls.length > 0) {
            console.log(`Found ${analysisResult.goodUrls.length} goodUrls in main analysis response`);
            setGoodUrlsFound(true);
          }
          
          // Check if analysis is complete
          if (analysisResult.status === 'complete' || (analysisResult.analysisTime !== undefined && analysisResult.analysisTime > 0)) {
            setLoading(false);
            setResult(analysisResult);
            setAnalysisCompleted(true);
          }
        }
      } catch (err) {
        // On error, we'll continue polling but update the error state
        console.error('Error polling for updates:', err);
        setError(err instanceof Error ? err.message : 'Error checking analysis status');
      }
    };
    
    // Try to get goodUrls separately if not yet found
    const fetchGoodUrls = async () => {
      if (!goodUrlsFound && requestId) {
        try {
          console.log(`Attempt #${pollCount + 1} to get goodUrls for ${requestId}`);
          // Type assertion to handle the extended response structure
          const goodUrlsResponse = await getGoodUrls(requestId) as any;
          
          // Accept both 'ok' and 'success' status values from the API
          if ((goodUrlsResponse.status === 'success' || goodUrlsResponse.status === 'ok') && 
              goodUrlsResponse.goodUrls && 
              goodUrlsResponse.goodUrls.length > 0) {
            
            console.log(`Found ${goodUrlsResponse.goodUrls.length} goodUrls in separate polling`);
            
            // Update the result with the goodUrls
            setResult(prevResult => {
              if (!prevResult) return null;
              return {
                ...prevResult,
                goodUrls: goodUrlsResponse.goodUrls
              };
            });
            
            setGoodUrlsFound(true);
            setProgressStage(`Identified ${goodUrlsResponse.goodUrls.length} valuable URLs for understanding the business`);
            setAnalysisProgress(prev => Math.max(prev, 40)); // Show at least 40% progress after goodURLs are loaded
            
            // Also check status to see if we're nearing completion
            try {
              const statusResponse = await getAnalysisStatus(requestId);
              if (statusResponse.status === 'completed' || 
                  statusResponse.status === 'page_analysis_completed' || 
                  statusResponse.status === 'ai_analysis_completed') {
                // Refresh the full analysis one more time
                fetchStatus();
              }
            } catch (statusErr) {
              console.error('Error checking status after finding goodUrls:', statusErr);
            }
          } else if (goodUrlsResponse.processLogs || goodUrlsResponse.logs) {
            // If we have logs but no goodUrls yet, check for progress in the logs
            const logs = goodUrlsResponse.processLogs || goodUrlsResponse.logs || [];
              
            const progressLogs = logs.filter(
              (log: any) => log.message && log.message.includes('goodUrls')
            );
            
            if (progressLogs.length > 0) {
              // Found some logs about goodUrls processing
              const latestLog = progressLogs[progressLogs.length - 1];
              setProgressStage(latestLog.message);
              
              // If we see logs about OpenAI, update progress indicator
              if (latestLog.message.includes('OpenAI')) {
                setAnalysisProgress(prev => Math.max(prev, 20));
              }
              // If we see logs about filtering, update progress indicator
              else if (latestLog.message.includes('filter')) {
                setAnalysisProgress(prev => Math.max(prev, 15));
              }
              // Show we're making progress if we see any logs
              else {
                setAnalysisProgress(prev => Math.max(prev, 10));
              }
            }
          }
        } catch (err) {
          console.error('Error fetching goodUrls:', err);
          // Don't update error state to avoid stopping the main polling
        }
      }
    };
    
    // Fetch both status and goodUrls
    const fetchAll = async () => {
      pollCount++;
      await fetchStatus();
      // Only try to fetch goodUrls if we haven't found them yet
      if (!goodUrlsFound) {
        await fetchGoodUrls();
      }
    };
    
    // Initial check
    fetchAll();
    
    // Calculate polling interval - faster at first, then slower
    const getPollInterval = () => {
      const elapsedTime = Date.now() - pollingStartTime;
      
      // First minute: poll every 1 second to quickly get goodUrls
      if (elapsedTime < 60000) return 1000;
      
      // 1-3 minutes: poll every 2 seconds
      if (elapsedTime < 180000) return 2000;
      
      // After 3 minutes: standard 3-second polling
      return 3000;
    };
    
    // Set up adaptive polling interval
    const intervalId = setInterval(() => {
      fetchAll();
    }, getPollInterval());
    
    return () => clearInterval(intervalId);
  }, [requestId, loading, analysisCompleted, goodUrlsFound]);
  
  // Update countdown timer
  useEffect(() => {
    if (!loading || !lastUpdateTime) return;
    
    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - lastUpdateTime.getTime()) / 1000);
      const remaining = Math.max(0, 3 - elapsed);
      setCountdown(remaining);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [loading, lastUpdateTime]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setErrorDetails(null);
    setResult(null);
    setRequestId(null);
    setAnalysisProgress(0);
    setProgressStage('Initializing analysis...');
    setLastUpdateTime(null);
    setAnalysisCompleted(false);
    setGoodUrlsFound(false);

    try {
      const response = await analyzeWebsite({
        url,
        numPages: parseInt(numPages, 10),
        includeHtml,
        includeFullText,
        requestId: customRequestId || undefined,
        forceRefresh
      });

      if (response) {
        if (response.status === 'complete') {
          // Analysis completed immediately
          setResult(response);
          setLoading(false);
          setAnalysisCompleted(true);
          // Check if response has goodUrls
          if (response.goodUrls && response.goodUrls.length > 0) {
            setGoodUrlsFound(true);
          }
        } else if (response.requestId) {
          // Analysis is running, store the requestId for polling
          setRequestId(response.requestId);
          setLastUpdateTime(new Date());
          
          // Check if initial response already has goodUrls
          if (response.goodUrls && response.goodUrls.length > 0) {
            console.log(`Initial response already has ${response.goodUrls.length} goodUrls`);
            setGoodUrlsFound(true);
          } else {
            // Immediately try to fetch GoodURLs rather than waiting for polling
            try {
              console.log(`Immediately fetching goodUrls for new requestId: ${response.requestId}`);
              const goodUrlsResponse = await getGoodUrls(response.requestId);
              
              // Accept both 'ok' and 'success' status values from the API
              if ((goodUrlsResponse.status === 'success' || goodUrlsResponse.status === 'ok') && 
                  goodUrlsResponse.goodUrls && 
                  goodUrlsResponse.goodUrls.length > 0) {
                
                console.log(`Found ${goodUrlsResponse.goodUrls.length} goodUrls immediately after starting analysis`);
                
                // Update the result with the goodUrls
                setResult(prevResult => {
                  if (!prevResult) return {
                    ...response,
                    goodUrls: goodUrlsResponse.goodUrls
                  };
                  
                  return {
                    ...prevResult,
                    goodUrls: goodUrlsResponse.goodUrls
                  };
                });
                
                setGoodUrlsFound(true);
              }
            } catch (goodUrlsErr) {
              console.log('Initial goodUrls fetch failed, will try again during polling:', goodUrlsErr);
              // Don't set error state, just continue with regular polling
            }
          }
        }
      }
    } catch (err) {
      setLoading(false);
      
      // Extract detailed error information
      if (err instanceof Error) {
        setError(err.message);
        
        // Check if we have enhanced error properties
        const enhancedError = err as any;
        if (enhancedError.statusCode || enhancedError.requestPayload || enhancedError.responseData) {
          setErrorDetails({
            statusCode: enhancedError.statusCode,
            requestPayload: enhancedError.requestPayload,
            responseData: enhancedError.responseData
          });
        }
      } else {
        setError('An error occurred during analysis');
      }
    }
  };

  // Common text field styles

  // Common switch label styles

  return (
    <Container maxWidth="xl">
      <Box sx={{ 
        py: 2,
        backgroundColor: theme.palette.background.default,
        minHeight: 'calc(100vh - 150px)'
      }}>
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              bgcolor: 'rgba(18, 21, 40, 0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              mb: 3,
              width: '100%'
            }}
          >
            <form onSubmit={handleSubmit}>
              {/* Main input section */}
              <Grid container spacing={2} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    id="url"
                    name="url"
                    label="Website URL"
                    variant="outlined"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                    autoFocus
                    required
                    inputProps={{
                      sx: { color: 'white' }
                    }}
                    InputLabelProps={{
                      sx: { color: 'rgba(255, 255, 255, 0.7)' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                      mb: 3
                    }}
                  />
                </Grid>
                
                <Grid item xs={6} md={2}>
                  <TextField
                    fullWidth
                    id="numPages"
                    name="numPages"
                    label="Secondary Pages"
                    variant="outlined"
                    type="number"
                    inputProps={{
                      min: 0,
                      max: 3,
                      sx: { color: 'white' }
                    }}
                    InputLabelProps={{
                      sx: { color: 'rgba(255, 255, 255, 0.7)' }
                    }}
                    value={numPages}
                    onChange={(e) => setNumPages(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={6} md={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', pt: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={forceRefresh}
                          onChange={(e) => setForceRefresh(e.target.checked)}
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 63, 122, 0.08)',
                              },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>
                          Force Refresh
                          <Tooltip title="Force a fresh analysis instead of using cached data. This will take longer but ensures the most up-to-date results.">
                            <IconButton size="small" sx={{ ml: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Box>
                </Grid>
              </Grid>
              
              {/* Advanced options section */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <Divider sx={{ 
                  my: 2, 
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  '&::before, &::after': {
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                    Advanced Options
                  </Typography>
                </Divider>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeHtml}
                          onChange={(e) => setIncludeHtml(e.target.checked)}
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 63, 122, 0.08)',
                              },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>
                          Include HTML
                          <Tooltip title="Include full HTML content in the analysis result. This increases the response size but may be useful for debugging.">
                            <IconButton size="small" sx={{ ml: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeFullText}
                          onChange={(e) => setIncludeFullText(e.target.checked)}
                          color="primary"
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 63, 122, 0.08)',
                              },
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: theme.palette.primary.main,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ color: 'white', display: 'flex', alignItems: 'center' }}>
                          Include Full Text
                          <Tooltip title="Include the full extracted text content in the analysis result. This increases the response size but may provide more context.">
                            <IconButton size="small" sx={{ ml: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}>
                              <HelpOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      }
                    />
                  </Box>
                  
                  <TextField
                    label="Custom Request ID (Optional)"
                    variant="outlined"
                    value={customRequestId}
                    onChange={(e) => setCustomRequestId(e.target.value)}
                    inputProps={{
                      sx: { color: 'white' }
                    }}
                    InputLabelProps={{
                      sx: { color: 'rgba(255, 255, 255, 0.7)' }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      }
                    }}
                  />
                </Stack>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <BoltIcon />}
                  sx={{ 
                    minWidth: 200,
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                    },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(255, 63, 122, 0.3)',
                      color: 'rgba(255, 255, 255, 0.6)',
                    }
                  }}
                >
                  {loading ? 'Analyzing...' : 'Analyze Website'}
                </Button>
              </Box>
            </form>
          </Paper>
        </motion.div>

        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 3,
                bgcolor: 'rgba(18, 21, 40, 0.5)',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                mb: 3,
                textAlign: 'center'
              }}
            >
              <Box sx={{ width: '100%', mb: 3 }}>
                <LinearProgress 
                  color="primary" 
                  variant={analysisProgress > 0 ? "determinate" : "indeterminate"}
                  value={analysisProgress}
                  sx={{ 
                    borderRadius: 5,
                    height: 10,
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: theme.palette.primary.main
                    }
                  }}
                />
              </Box>
              <Typography variant="h6" color="white" gutterBottom>
                Analyzing Website {analysisProgress > 0 ? `(${Math.round(analysisProgress)}%)` : ''}
              </Typography>
              <Typography variant="body2" color="rgba(255, 255, 255, 0.9)" sx={{ maxWidth: 600, mx: 'auto', mb: 2 }}>
                {progressStage}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
                {lastUpdateTime && (
                  <Chip
                    icon={<TimerIcon fontSize="small" />}
                    label={`Next update in ${countdown}s`}
                    color="default"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white'
                    }}
                  />
                )}
                {lastUpdateTime && (
                  <Chip
                    icon={<AutorenewIcon fontSize="small" />}
                    label={`Last updated: ${lastUpdateTime.toLocaleTimeString()}`}
                    color="default"
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: 'white'
                    }}
                  />
                )}
              </Box>
            </Paper>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Alert 
              severity="error" 
              sx={{ 
                mb: 2, 
                backgroundColor: 'rgba(211, 47, 47, 0.1)', 
                color: 'white',
                border: '1px solid rgba(211, 47, 47, 0.5)',
                '.MuiAlert-icon': {
                  color: '#f44336',
                }
              }}
            >
              <div>
                <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                  <strong>{errorDetails?.statusCode ? `Error ${errorDetails.statusCode}: ` : ''}</strong>{error}
                </Typography>
                
                {errorDetails && errorDetails.responseData && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                      Error Analysis:
                    </Typography>
                    
                    <Alert severity="warning" sx={{ mb: 2, backgroundColor: 'rgba(237, 108, 2, 0.1)', color: 'white' }}>
                      <Typography variant="body2">
                        <strong>Technical Issue:</strong> The website is likely using security measures that block our crawler.
                        We should be seeing HTML content even for blocked sites, but none was returned.
                      </Typography>
                    </Alert>
                    
                    <Grid container spacing={2}>
                      {/* Left Column */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0,0,0,0.2)', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 1,
                          height: '100%'
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                            Request Details
                          </Typography>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              URL:
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              {errorDetails.responseData.url}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Request ID:
                            </Typography>
                            <Typography variant="body2">
                              {errorDetails.responseData.requestId}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Error Code:
                            </Typography>
                            <Typography variant="body2">
                              {errorDetails.responseData.errorCode}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Analysis Time:
                            </Typography>
                            <Typography variant="body2">
                              {errorDetails.responseData.analysisTime}ms
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                      
                      {/* Right Column */}
                      <Grid item xs={12} md={6}>
                        <Paper sx={{ 
                          p: 2, 
                          backgroundColor: 'rgba(0,0,0,0.2)', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 1,
                          height: '100%'
                        }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                            Main Error
                          </Typography>
                          
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Status:
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: errorDetails.responseData.status === 'security_blocked' ? '#ff9800' : 'white'
                            }}>
                              {errorDetails.responseData.status}
                            </Typography>
                          </Box>
                          
                          <Box>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Message:
                            </Typography>
                            <Typography variant="body2">
                              {errorDetails.responseData.message}
                            </Typography>
                          </Box>

                          {/* User-friendly error message section */}
                          {errorDetails.responseData.userFriendlyMessage && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                              <Alert severity="warning" sx={{ backgroundColor: 'rgba(255, 152, 0, 0.1)', color: 'white', border: '1px solid rgba(255, 152, 0, 0.2)' }}>
                                <AlertTitle>Website Analysis Issue</AlertTitle>
                                <Typography variant="body2">
                                  {errorDetails.responseData.userFriendlyMessage}
                                </Typography>
                                {errorDetails.responseData.suggestionMessage && (
                                  <Typography variant="body2" sx={{ mt: 1 }}>
                                    {errorDetails.responseData.suggestionMessage}
                                  </Typography>
                                )}
                              </Alert>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    </Grid>
                    
                    {/* Process Logs */}
                    {errorDetails.responseData.processLogs && (
                      <Accordion 
                        sx={{ 
                          mt: 2,
                          backgroundColor: 'rgba(0,0,0,0.2)', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 1,
                          mb: 2,
                          '&:before': {
                            display: 'none',
                          },
                        }}
                        defaultExpanded
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                          sx={{ 
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Process Logs ({errorDetails.responseData.processLogs.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box sx={{ maxHeight: '200px', overflow: 'auto' }}>
                            {errorDetails.responseData.processLogs.map((log: any, index: number) => (
                              <Paper 
                                key={index}
                                sx={{ 
                                  p: 1, 
                                  mb: 1, 
                                  backgroundColor: 
                                    log.type === 'error' ? 'rgba(229, 57, 53, 0.1)' :
                                    log.type === 'warning' ? 'rgba(255, 152, 0, 0.1)' :
                                    log.type === 'success' ? 'rgba(76, 175, 80, 0.1)' :
                                    'rgba(33, 150, 243, 0.1)',
                                  border: 
                                    log.type === 'error' ? '1px solid rgba(229, 57, 53, 0.3)' :
                                    log.type === 'warning' ? '1px solid rgba(255, 152, 0, 0.3)' :
                                    log.type === 'success' ? '1px solid rgba(76, 175, 80, 0.3)' :
                                    '1px solid rgba(33, 150, 243, 0.3)',
                                }}
                              >
                                <Typography sx={{ 
                                  color: 'rgba(255, 255, 255, 0.9)', 
                                  fontSize: '0.8rem',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <span style={{ 
                                    opacity: 0.7, 
                                    marginRight: '8px', 
                                    fontSize: '0.75rem' 
                                  }}>
                                    {new Date(log.timestamp).toLocaleTimeString()}
                                  </span>
                                  {log.message}
                                </Typography>
                              </Paper>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                    
                    {/* Technical Details (Full Response Data) */}
                    <Accordion 
                      sx={{ 
                        backgroundColor: 'rgba(0,0,0,0.2)', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 1,
                        '&:before': {
                          display: 'none',
                        },
                      }}
                      defaultExpanded={errorDetails.responseData?.message?.includes("Insufficient text content") || errorDetails.responseData?.message?.includes("Failed to retrieve screenshot")}
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                        sx={{ 
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Complete Response (JSON)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <JsonViewer data={errorDetails.responseData} />
                      </AccordionDetails>
                    </Accordion>
                    
                    {/* Raw Request */}
                    {errorDetails.requestPayload && (
                      <Accordion 
                        sx={{ 
                          mt: 2,
                          backgroundColor: 'rgba(0,0,0,0.2)', 
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: 1,
                          '&:before': {
                            display: 'none',
                          },
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                          sx={{ 
                            borderBottom: '1px solid rgba(255,255,255,0.1)',
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                            Original Request
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Typography 
                            component="pre" 
                            sx={{ 
                              fontSize: '0.8rem', 
                              margin: 0, 
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word'
                            }}
                          >
                            {errorDetails.requestPayload}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}

                    {/* Missing Raw APIFlash Data Note */}
                    <Alert severity="info" sx={{ mt: 2, backgroundColor: 'rgba(33, 150, 243, 0.1)', color: 'white' }}>
                      <Typography variant="body2">
                        <strong>Note:</strong> The API response doesn't include the raw HTML/text from APIFlash. 
                        This would be useful for debugging. Consider updating the backend to include this data
                        even when errors occur.
                      </Typography>
                    </Alert>
                    
                    {/* APIFlash Text Extract - Even for errors, try to show any text that might be available */}
                    <Accordion 
                      sx={{ 
                        mt: 2,
                        backgroundColor: 'rgba(0,0,0,0.2)', 
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 1,
                        '&:before': {
                          display: 'none',
                        },
                      }}
                      defaultExpanded
                    >
                      <AccordionSummary 
                        expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                        sx={{ 
                          borderBottom: '1px solid rgba(255,255,255,0.1)',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Extracted Text
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Main page text if available */}
                        {errorDetails.responseData.errorDetails?.apiflashResponse?.mainPage?.text ? (
                          <Paper 
                            sx={{ 
                              p: 2, 
                              backgroundColor: 'rgba(0,0,0,0.3)', 
                              border: '1px solid rgba(255,255,255,0.1)',
                              borderRadius: 1,
                              mb: 2
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                              Main Page Text ({errorDetails.responseData.errorDetails.apiflashResponse.mainPage.text.length} characters)
                            </Typography>
                            <Box 
                              sx={{ 
                                maxHeight: '200px', 
                                overflow: 'auto', 
                                p: 1,
                                backgroundColor: 'rgba(0,0,0,0.2)',
                                borderRadius: 1
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                component="pre"
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontSize: '0.8rem',
                                  margin: 0
                                }}
                              >
                                {errorDetails.responseData.errorDetails.apiflashResponse.mainPage.text || 'No text extracted'}
                              </Typography>
                            </Box>
                          </Paper>
                        ) : (
                          <Alert severity="warning" sx={{ backgroundColor: 'rgba(237, 108, 2, 0.1)', color: 'white' }}>
                            <Typography variant="body2">
                              No text content was extracted from the page. This confirms the "Insufficient text content" error.
                            </Typography>
                          </Alert>
                        )}
                        
                        {/* Secondary pages text if available */}
                        {errorDetails.responseData.errorDetails?.apiflashResponse?.secondaryPages?.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                              Secondary Pages Text
                            </Typography>
                            
                            {errorDetails.responseData.errorDetails.apiflashResponse.secondaryPages.map((page: any, index: number) => (
                              <Accordion 
                                key={index}
                                sx={{ 
                                  mb: 1,
                                  backgroundColor: 'rgba(0,0,0,0.3)', 
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  borderRadius: 1,
                                  '&:before': {
                                    display: 'none',
                                  },
                                }}
                              >
                                <AccordionSummary 
                                  expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                                  sx={{ 
                                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                                  }}
                                >
                                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                    Page {index + 1}: {page.url} ({page.text?.length || 0} characters)
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Box 
                                    sx={{ 
                                      maxHeight: '200px', 
                                      overflow: 'auto', 
                                      p: 1,
                                      backgroundColor: 'rgba(0,0,0,0.2)',
                                      borderRadius: 1
                                    }}
                                  >
                                    <Typography 
                                      variant="body2" 
                                      component="pre"
                                      sx={{ 
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        fontSize: '0.8rem',
                                        margin: 0
                                      }}
                                    >
                                      {page.text || 'No text extracted'}
                                    </Typography>
                                  </Box>
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                )}
              </div>
            </Alert>
          </motion.div>
        )}

        {analysisCompleted && result && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                mb: 3,
                borderRadius: 3,
                bgcolor: 'rgba(46, 125, 50, 0.1)',
                border: '1px solid rgba(46, 125, 50, 0.2)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleOutlineIcon sx={{ color: 'success.main' }} />
                <Typography variant="body1" color="white">
                  Analysis completed in {result.analysisTime ? formatTime(result.analysisTime) : formatTime(result.duration || 0)}
                </Typography>
              </Box>
            </Paper>
          </motion.div>
        )}

        {result && (
          <Box sx={{ mt: 5, mb: 5 }}>
            <AnalysisResults result={result} />
          </Box>
        )}
      </Box>
    </Container>
  );
} 